// Drip Sequences data + mutations (PRD §4.4 / WP-09).
// Read from tenants/{t}/sequences; create/update/delete/enroll via owner/admin callables.
import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from './firebase';
import { useTenant } from './tenant';
import { call } from './callable';

export type SequenceType = 'time_based' | 'action_based';
export type ReEntryPolicy = 'never' | 'after_completion' | 'always';

export interface DeliveryWindow {
  preferredTime: string; // HH:mm
  timezone: string;
}

export interface SequenceStep {
  id: string;
  delayHours: number;
  templateName: string;
  templateLanguage: string;
}

export interface Sequence {
  id: string;
  name: string;
  enabled: boolean;
  type: SequenceType;
  reEntryPolicy: ReEntryPolicy;
  deliveryWindow?: DeliveryWindow | null;
  steps: SequenceStep[];
  enrolled: number;
  completed: number;
  lastSentAt?: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface SeqInput {
  id?: string;
  name: string;
  enabled: boolean;
  type: SequenceType;
  reEntryPolicy: ReEntryPolicy;
  deliveryWindow?: DeliveryWindow | null;
  steps: SequenceStep[];
}

export function useSequences(): { sequences: Sequence[]; loading: boolean } {
  const { tenantId } = useTenant();
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) {
      setSequences([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, `tenants/${tenantId}/sequences`),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(
      q,
      (snap) => {
        setSequences(
          snap.docs.map((d) => {
            const s = d.data() as Partial<Sequence>;
            return {
              id: d.id,
              name: s.name ?? '',
              enabled: s.enabled ?? false,
              type: s.type ?? 'time_based',
              reEntryPolicy: s.reEntryPolicy ?? 'never',
              deliveryWindow: s.deliveryWindow ?? null,
              steps: s.steps ?? [],
              enrolled: s.enrolled ?? 0,
              completed: s.completed ?? 0,
              lastSentAt: s.lastSentAt ?? null,
              createdAt: s.createdAt ?? 0,
              updatedAt: s.updatedAt ?? 0,
            };
          })
        );
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [tenantId]);

  return { sequences, loading };
}

// ── Callables ────────────────────────────────────────────────────────────────

export const sequenceSave = call<SeqInput, { ok: boolean; id: string }>('sequenceSave');
export const sequenceDelete = call<{ id: string }, { ok: boolean }>('sequenceDelete');
export const sequenceEnroll = call<
  { sequenceId: string; subscriberIds: string[] },
  { ok: boolean; enrolled: number; skipped?: number }
>('sequenceEnroll');
export const sequenceList = call<{}, { ok: boolean; sequences: Sequence[] }>('sequenceList');
export const sequenceGet = call<{ id: string }, { ok: boolean; sequence: Sequence }>('sequenceGet');

// Helper aliases
export const saveSequence = async (input: SeqInput) => (await sequenceSave(input)).data;
export const deleteSequence = async (id: string) => (await sequenceDelete({ id })).data;
export const enrollSubscribers = async (sequenceId: string, subscriberIds: string[]) =>
  (await sequenceEnroll({ sequenceId, subscriberIds })).data;
